import React from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';

const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  color, 
  subtitle, 
  onClick, 
  isLoaded = true,
  useCountUp = true,
  countUpProps = {},
  className = "",
  variant = "default" // default, compact, detailed
}) => {
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const renderValue = () => {
    if (useCountUp && isLoaded && typeof value === 'number') {
      return (
        <CountUp
          end={value}
          duration={2}
          separator=","
          {...countUpProps}
        />
      );
    }
    return value;
  };

  if (variant === "compact") {
    return (
      <motion.div
        variants={itemVariants}
        className={`bg-white rounded-lg p-4 shadow-sm border border-gray-100 ${onClick ? 'cursor-pointer hover:shadow-md' : ''} ${className}`}
        whileHover={onClick ? { scale: 1.02 } : {}}
        whileTap={onClick ? { scale: 0.98 } : {}}
        onClick={onClick}
      >
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${color} mr-3`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-600 truncate">{title}</p>
            <p className="text-lg font-bold text-gray-900">{renderValue()}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (variant === "detailed") {
    return (
      <motion.div
        variants={itemVariants}
        className={`bg-white rounded-xl p-6 shadow-lg border border-gray-100 ${onClick ? 'cursor-pointer card-hover' : ''} ${className}`}
        whileHover={onClick ? { scale: 1.02 } : {}}
        whileTap={onClick ? { scale: 0.98 } : {}}
        onClick={onClick}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
            <div className="text-4xl font-bold text-gray-900 mb-2">
              {renderValue()}
            </div>
            {subtitle && (
              <p className="text-sm text-gray-500">{subtitle}</p>
            )}
          </div>
          <div className={`p-4 rounded-full ${color}`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
        </div>
        
        {/* Optional trend indicator or additional info */}
        <div className="pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-400">Click to view details</p>
        </div>
      </motion.div>
    );
  }

  // Default variant
  return (
    <motion.div
      variants={itemVariants}
      className={`bg-white rounded-xl p-6 shadow-lg border border-gray-100 ${onClick ? 'cursor-pointer card-hover' : ''} ${className}`}
      whileHover={onClick ? { scale: 1.02 } : {}}
      whileTap={onClick ? { scale: 0.98 } : {}}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <div className="text-3xl font-bold text-gray-900 mb-1">
            {renderValue()}
          </div>
          {subtitle && (
            <p className="text-xs text-gray-500">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );
};

export default StatCard;